import { useMemo } from "react";
import { DateTime } from "luxon";
import { graphql, useFragment } from "react-relay";
import { useNowIndexes_teamScheduleEventOccasion$key } from "./__generated__/useNowIndexes_teamScheduleEventOccasion.graphql";

export function useNowIndexes({
  eventKey,
  now = DateTime.now(),
}: {
  eventKey: useNowIndexes_teamScheduleEventOccasion$key;
  now?: DateTime;
}): [number[], number] {
  const events = useFragment(
    graphql`
      fragment useNowIndexes_teamScheduleEventOccasion on TeamScheduleEventOccasion
      @relay(plural: true) {
        startAt
      }
    `,
    eventKey
  );

  return useMemo(() => {
    if (events.length === 0) {
      return [[], 0];
    }

    const firstIndexStartingAfterNow = events.findIndex((e) => {
      if (e.startAt) {
        return DateTime.fromISO(e.startAt) > now;
      }
      return false;
    });

    if (firstIndexStartingAfterNow === 0) {
      return [[-1], 0];
    }

    if (firstIndexStartingAfterNow === -1) {
      const lastEvent = events[events.length - 1];
      if (DateTime.fromISO(lastEvent.startAt).plus({ hours: 2 }) > now) {
        return [[events.length - 1], events.length - 2];
      }
      return [[], events.length - 1];
    }

    const lastNowIndex = firstIndexStartingAfterNow - 1;
    const lastNowItem = events[lastNowIndex];

    const lastNowTime = DateTime.fromISO(lastNowItem.startAt).startOf("minute");
    // find first item with same time as last now item
    const nowIndexes = events
      .map((item, i) => [item, i] as const)
      .filter(([e]) => {
        return (
          Math.abs(
            DateTime.fromISO(e.startAt)
              .startOf("minute")
              .diff(lastNowTime)
              .as("minutes")
          ) < 1
        );
      })
      .map(([_, i]) => i);
    const scrollIndex = Math.max(nowIndexes[0] ?? 0, 0);
    return [nowIndexes, scrollIndex];
  }, [events, now]);
}
